import { addBusinessDays } from 'date-fns'

import { ContractType } from '@/pages/instruments/new-instrument/constants/contract-types'
import {
  BarrierLegParams,
  ObservationFrequency,
  PrimaryTerminalLegParams,
  ScheduleGenerator,
  ScheduleLeg,
  Strike,
} from '@/pages/instruments/new-instrument/schedule-builders/types'

export type RangeAccrualOptions = {
  strikes: Strike[]
  initial_observation_date: Date
  maturity_date: Date
  settlement_cycle: number
  coupon_rate_annual: number
  coupon_yearly_observations: ObservationFrequency
  knockdown_input_level: number | null
  contract_type: ContractType
  currency_id: number
}

const lowerBoundThreshold = 90
const upperBoundThreshold = 120

const buildPrimaryLowerLeg = (
  params: PrimaryTerminalLegParams<RangeAccrualOptions>,
): ScheduleLeg => {
  const {
    strike,
    initial_observation_date,
    currency_id,
    contract_type,
    maturity_date,
    settlement_cycle,
  } = params

  return {
    option_type: 'Vanilla',
    region_type: 'European',
    contract_type,
    currency_id,
    strike: strike.value,
    instrument_id: strike.underlying_id,
    strike_percent: lowerBoundThreshold,
    participation_rate: 1,
    initial_observation_date,
    pay_date: addBusinessDays(maturity_date, settlement_cycle),
    start_observation_date: maturity_date,
    end_observation_date: maturity_date,
  }
}

const buildPrimaryUpperLeg = (
  params: PrimaryTerminalLegParams<RangeAccrualOptions>,
): ScheduleLeg => {
  const {
    strike,
    initial_observation_date,
    currency_id,
    contract_type,
    maturity_date,
    settlement_cycle,
  } = params

  return {
    option_type: 'Vanilla',
    region_type: 'European',
    contract_type,
    currency_id,
    strike: strike.value,
    instrument_id: strike.underlying_id,
    strike_percent: upperBoundThreshold,
    participation_rate: 1,
    initial_observation_date,
    pay_date: addBusinessDays(maturity_date, settlement_cycle),
    start_observation_date: maturity_date,
    end_observation_date: maturity_date,
  }
}

const buildBarrierLeg = (params: BarrierLegParams<RangeAccrualOptions>): ScheduleLeg => {
  const { currency_id, final_leg, initial_observation_date, knockdown_input_level, strike } = params

  return {
    option_type: 'Barrier Vanilla',
    barrier_type: 'Down-and-In',
    contract_type: 'Put',
    region_type: 'European',
    currency_id,
    participation_rate: -1,
    strike: strike.value,
    strike_percent: strike.percent,
    pay_date: final_leg.pay_date,
    start_observation_date: final_leg.start_observation_date,
    end_observation_date: final_leg.end_observation_date,
    instrument_id: strike.underlying_id,
    payoff_barrier_level: knockdown_input_level,
    initial_observation_date: initial_observation_date,
  }
}

export const RangeAccrualGenerator: ScheduleGenerator<RangeAccrualOptions> = {
  generate: ({ strikes, ...params }) => {
    const legs: ScheduleLeg[] = []

    for (const strike of strikes) {
      const lowerPrimaryLeg = buildPrimaryLowerLeg({ strike, ...params })
      const upperPrimaryLeg = buildPrimaryUpperLeg({ strike, ...params })

      legs.push({ ...lowerPrimaryLeg, leg_id: 1 }, { ...upperPrimaryLeg, leg_id: 2 })

      if (params.knockdown_input_level !== null) {
        legs.push({
          ...buildBarrierLeg({ strike, final_leg: upperPrimaryLeg, ...params }),
          leg_id: 3,
        })
      }
    }

    return legs
  },
}
